import { useCallback } from 'react';

import { useCreateSurvey } from '@helpful/network';
import { useContextSelector } from 'use-context-selector';
import { RoutePath } from '@common/constants';
import { useRoute } from '@core/navigation';

import { DetailsContext } from './Details.context';

interface UseNavigateNextParams {
  id: string;
  parentId?: string;
  handleSuccess: boolean;
}

type UseNavigateReturnType = [{}, () => Promise<void>];

export const useNavigateNext = ({
  id,
  parentId,
  handleSuccess,
}: UseNavigateNextParams): UseNavigateReturnType => {
  const { mutateAsync: create } = useCreateSurvey();
  const [, { push }] = useRoute();

  const survey = useContextSelector(DetailsContext, ({ survey }) => survey);

  const navigate = useCallback(async () => {
    if (!!survey?.success && handleSuccess) {
      push({
        search: { id, parentId: parentId || id },
        path: RoutePath.SURVEYS_DETAILS_SUCCESS,
        params: { slug: survey?.slug },
      });
      return;
    }
    if (!!survey?.nextSurvey?.slug) {
      await create(
        {
          data: {
            parentId: parentId || id,
            slug: survey?.nextSurvey?.slug,
          },
        },
        {
          onSuccess: ({ data: { id } }) => {
            push({
              search: { id, parentId: parentId || id },
              path: RoutePath.SURVEYS_DETAILS_QUESTIONS,
              params: { slug: survey?.nextSurvey?.slug },
            });
          },
        }
      );
      return;
    }
    if (!!survey?.nextScreen?.url) {
      push({
        path: survey?.nextScreen?.url,
        search: { id, parentId: parentId || id },
      });
      return;
    }
    if (!survey?.nextUrl) {
      push({ path: RoutePath.ROOT_HOME });
      return;
    }
  }, [survey, create, id, push, handleSuccess, parentId]);

  return [{}, navigate];
};
